// Data API Service (Web)
// Uses cookie-based authentication (browser flow with Kratos/Oathkeeper)

import { apolloClient } from '../graphql/client';
import { GET_ALL_FAMILY_DATA, GET_PERSON_DATA } from '@common/graphql';

const cache = apolloClient.cache;

const IDENTITY_API_URL =
  process.env.REACT_APP_IDENTITY_API_URL ||
  'https://api.familypal.com/familypal/v1/identity';

async function identityApi(path: string, method: string, body?: any) {
  const res = await fetch(`${IDENTITY_API_URL}${path}`, {
    method,
    credentials: 'include', // Send cookies for auth
    headers: {
      'Content-Type': 'application/json',
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `API error ${res.status}`);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return null;
  }
  return res.json();
}

/**
 * Update a family_data item in the Apollo cache (GET_ALL_FAMILY_DATA).
 */
function updateFamilyDataCache(
  action: 'insert' | 'update' | 'delete',
  dataId: string,
  type?: string,
  data?: any,
  familyId?: string,
) {
  try {
    const existing = cache.readQuery<any>({ query: GET_ALL_FAMILY_DATA });
    if (!existing?.family_data) return;

    let updated: any[];
    const now = new Date().toISOString();

    if (action === 'delete') {
      updated = existing.family_data.filter((item: any) => item.data?.id !== dataId);
    } else if (action === 'update') {
      updated = existing.family_data.map((item: any) =>
        item.data?.id === dataId
          ? { ...item, modified_at: now, data: { ...item.data, type, data } }
          : item
      );
    } else {
      // insert
      const newItem = {
        __typename: 'family_data',
        id: dataId,
        family_id: familyId,
        status: 'A',
        created_at: now,
        modified_at: now,
        data: {
          __typename: 'data',
          id: dataId,
          type,
          data,
        },
      };
      updated = [newItem, ...existing.family_data];
    }

    cache.writeQuery({ query: GET_ALL_FAMILY_DATA, data: { family_data: updated } });
  } catch (e) {
    // Cache miss — query not in cache yet, nothing to update
  }
}

/**
 * Update person_data items in the Apollo cache (GET_PERSON_DATA).
 */
function updatePersonDataCache(
  action: 'insert' | 'update' | 'delete',
  opts: {
    linkId?: string;
    dataId: string;
    personId?: string;
    personIds?: string[];
    type?: string;
    data?: any;
  },
) {
  const now = new Date().toISOString();

  const apply = (items: any[]): any[] => {
    if (action === 'delete') {
      if (opts.linkId) {
        return items.filter((item: any) => item.id !== opts.linkId);
      }
      if (opts.personIds) {
        return items.filter(
          (item: any) => !(item.data?.id === opts.dataId && opts.personIds!.includes(item.person_id))
        );
      }
      return items.filter((item: any) => item.data?.id !== opts.dataId);
    } else if (action === 'update') {
      return items.map((item: any) =>
        item.data?.id === opts.dataId
          ? { ...item, modified_at: now, data: { ...item.data, type: opts.type, data: opts.data } }
          : item
      );
    } else {
      // insert
      const ids = opts.personIds || (opts.personId ? [opts.personId] : []);
      const newItems = ids.map((pid) => ({
        __typename: 'person_data',
        id: opts.linkId || opts.dataId,
        person_id: pid,
        status: 'A',
        created_at: now,
        modified_at: now,
        data: {
          __typename: 'data',
          id: opts.dataId,
          type: opts.type,
          data: opts.data,
        },
      }));
      return [...newItems, ...items];
    }
  };

  // Update GET_PERSON_DATA for each relevant person
  const personIds = opts.personIds || (opts.personId ? [opts.personId] : []);

  if (personIds.length === 0) {
    // No person IDs provided (e.g. updateData called without them) —
    // evict all person_data to force lazy refetch on next access
    cache.evict({ fieldName: 'person_data' });
    cache.gc();
    return;
  }

  for (const pid of personIds) {
    try {
      const existing = cache.readQuery<any>({
        query: GET_PERSON_DATA,
        variables: { person_id: pid },
      });
      if (existing?.person_data) {
        cache.writeQuery({
          query: GET_PERSON_DATA,
          variables: { person_id: pid },
          data: { person_data: apply(existing.person_data) },
        });
      }
    } catch (e) {
      // Cache miss
    }
  }

  // For insert/delete, also evict GET_FAMILY_MEMBERS_DATA since we don't know
  // which variable sets are cached
  if (action !== 'update') {
    cache.evict({ fieldName: 'person_data' });
    cache.gc();
  }
}

/** Update a data record by ID (used for both family_data and person_data) */
export async function updateData(id: string, type: string, data: any) {
  const result = await identityApi(`/data/${id}`, 'PUT', {
    id,
    type,
    data: JSON.stringify(data),
  });
  updateFamilyDataCache('update', id, type, data);
  updatePersonDataCache('update', { dataId: id, type, data });
  return result;
}

/** Soft-delete a person_data link by ID */
export async function deletePersonData(id: string) {
  const result = await identityApi(`/data/person/${id}`, 'DELETE');
  updatePersonDataCache('delete', { linkId: id, dataId: id });
  return result;
}

/** Soft-delete a family_data link by ID */
export async function deleteFamilyData(id: string) {
  const result = await identityApi(`/data/family/${id}`, 'DELETE');
  updateFamilyDataCache('delete', id);
  return result;
}

/** Insert a family_data record */
export async function insertFamilyData(familyId: string, type: string, data: any) {
  const result = await identityApi('/data/family', 'POST', {
    familyId,
    type,
    data: JSON.stringify(data),
  });
  if (result?.id) {
    updateFamilyDataCache('insert', result.id, type, data, familyId);
  }
  return result;
}

/** Insert a person_data record */
export async function insertPersonData(personId: string, type: string, data: any) {
  const result = await identityApi('/data/person', 'POST', {
    personId,
    type,
    data: JSON.stringify(data),
  });
  if (result?.dataId) {
    updatePersonDataCache('insert', {
      linkId: result.id,
      dataId: result.dataId,
      personId,
      type,
      data,
    });
  }
  return result;
}

/** Insert a shared data record linked to multiple people */
export async function insertSharedPersonData(type: string, data: any, personIds: string[]) {
  const result = await identityApi('/data/person/shared', 'POST', {
    type,
    data: JSON.stringify(data),
    personIds,
  });
  if (result?.id) {
    updatePersonDataCache('insert', {
      dataId: result.id,
      personIds,
      type,
      data,
    });
  }
  return result;
}

/** Add person links to an existing data record */
export async function insertPersonDataLinks(dataId: string, personIds: string[]) {
  const result = await identityApi('/data/person/links', 'POST', {
    dataId,
    personIds,
  });
  try {
    const existing = cache.readQuery<any>({ query: GET_ALL_FAMILY_DATA });
    const match = existing?.family_data?.find((item: any) => item.data?.id === dataId);
    if (match) {
      updatePersonDataCache('insert', {
        dataId,
        personIds,
        type: match.data.type,
        data: match.data.data,
      });
    } else {
      cache.evict({ fieldName: 'person_data' });
      cache.gc();
    }
  } catch (e) {
    cache.evict({ fieldName: 'person_data' });
    cache.gc();
  }
  return result;
}

/** Soft-delete person links from a data record */
export async function deletePersonDataLinks(dataId: string, personIds: string[]) {
  const result = await identityApi('/data/person/links/delete', 'POST', {
    dataId,
    personIds,
  });
  updatePersonDataCache('delete', { dataId, personIds });
  return result;
}

export default {
  updateData,
  deletePersonData,
  deleteFamilyData,
  insertFamilyData,
  insertPersonData,
  insertSharedPersonData,
  insertPersonDataLinks,
  deletePersonDataLinks,
};
